import type { ComponentProps } from 'react';
import React, { forwardRef, useLayoutEffect, useState } from 'react';
import type { DistributedPick } from 'type-fest';
import type { ButtonProps } from './Button';
import Button from './Button';
import FloatingCard from './FloatingCard';
import HoverView from './HoverView';
import * as styles from './LargeTooltip.module.scss';
import SVGIcon from './SVGIcon';

type LargeTooltipButtonProps = DistributedPick<ButtonProps, 'to' | 'newTab'> & {
  onClick?: () => unknown;
  text: string;
  icon?: string;
};

type LargeTooltipContent =
  | {
      heading?: string;
      paragraph: React.ReactNode;
      button?: LargeTooltipButtonProps;
    }
  | string;

const calculateMaxWidth = (text: string) => {
  const words = text.trim().split(/\s+/).length;
  if (words >= 5 && words <= 20) {
    return 200;
  }
  if (words > 20) {
    return 300;
  }

  return undefined;
};

interface LargeTooltipContentProps
  extends Omit<ComponentProps<typeof FloatingCard>, 'content' | 'children'> {
  content: LargeTooltipContent;
}

const LargeTooltipContent = forwardRef<
  HTMLDivElement,
  LargeTooltipContentProps
>(({ content: contentFromProps, ...props }, ref) => {
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!containerElement) {
      return undefined;
    }

    const mutationObserver = new MutationObserver(() => {
      setMaxWidth(calculateMaxWidth(containerElement.textContent ?? ''));
    });

    setMaxWidth(calculateMaxWidth(containerElement.textContent ?? ''));
    mutationObserver.observe(containerElement, {
      subtree: true,
      childList: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [containerElement]);

  const content =
    typeof contentFromProps === 'string'
      ? { paragraph: contentFromProps }
      : contentFromProps;

  const { text, icon, ...buttonProps } = content.button ?? {};

  return (
    <FloatingCard ref={ref} {...props}>
      <div style={{ maxWidth }} className={styles.contentContainer}>
        {content.heading && (
          <h2 className={styles.heading}>{content.heading}</h2>
        )}
        <div ref={setContainerElement} className={styles.inner}>
          {content.paragraph}
        </div>
        {content.button && (
          <Button
            {...buttonProps}
            variant="secondary"
            size="small"
            className={styles.button}
          >
            <div>{text}</div>
            {!!icon && (
              <div>
                <SVGIcon size={12}>{icon}</SVGIcon>
              </div>
            )}
          </Button>
        )}
      </div>
    </FloatingCard>
  );
});

type LargeTooltipProps = {
  content: LargeTooltipContent | null;
  offset?: number;
  children: React.ReactNode;
} & DistributedPick<
  ComponentProps<typeof HoverView>,
  | 'placement'
  | 'flipBehavior'
  | 'offset'
  | 'open'
  | 'onOpenChange'
  | 'anchorElement'
>;

const LargeTooltip = ({
  content,
  children,
  placement = 'top',
  ...rest
}: LargeTooltipProps) => {
  if (!content) {
    return <>{children}</>;
  }

  return (
    <HoverView
      hoverGroupId="tooltip"
      view={<LargeTooltipContent content={content} />}
      placement={placement}
      {...rest}
    >
      {children}
    </HoverView>
  );
};

export default LargeTooltip;
